import React, { useContext, useState, useEffect, useRef, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import LanguageContext from '../../context/LanguageContext.js';
import { useStorefront } from '../../context/StoreContext.js';
import {
    fetchTestimonials,
    mapTestimonialRecord,
    sortTestimonials,
} from '../../api/testimonialsApi.js';
import { getTestimonialsPlaceholder } from '../../data/testimonialsPlaceholder.js';
import './Testimonials.css';

/** One color per avatar — used for both ring and initials */
const AVATAR_COLORS = [
    '#e8c97a',
    '#a8d8a8',
    '#f5a623',
    '#c9b1ff',
    '#7fffd4',
    '#ffb3b3',
    '#87ceeb',
    '#ffd700',
    '#f0abfc',
    '#86efac',
    '#fcd34d',
    '#93c5fd',
    '#fca5a5',
    '#5eead4',
    '#d8b4fe',
];

function getInitials(name) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

/** Cap how many dot indicators render; window slides with the active slide. */
const MAX_VISIBLE_DOTS = 7;

function getVisibleDotIndices(activeIndex, total) {
    if (total <= MAX_VISIBLE_DOTS) {
        return Array.from({ length: total }, (_, i) => i);
    }
    const half = Math.floor(MAX_VISIBLE_DOTS / 2);
    let start = activeIndex - half;
    if (start < 0) start = 0;
    if (start + MAX_VISIBLE_DOTS > total) start = total - MAX_VISIBLE_DOTS;
    return Array.from({ length: MAX_VISIBLE_DOTS }, (_, k) => start + k);
}

const StarRating = ({ rating = 5 }) => (
    <div className="testimonials-stars">
        {[...Array(5)].map((_, i) => (
            <Star
                key={i}
                size={14}
                fill={i < rating ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth={1.5}
                aria-hidden="true"
            />
        ))}
    </div>
);

const QuoteIcon = () => (
    <Quote className="testimonials-quote-icon" size={36} strokeWidth={1.25} aria-hidden="true" />
);

const ChevronIcon = ({ direction }) =>
    direction === 'prev' ? (
        <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
    ) : (
        <ChevronRight size={20} strokeWidth={2} aria-hidden="true" />
    );

function TestimonialAvatar({ item, index, sizeClass }) {
    const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

    if (item.avatarUrl) {
        return (
            <img
                src={item.avatarUrl}
                alt=""
                className={`testimonials-avatar ${sizeClass} testimonials-avatar--photo`}
            />
        );
    }

    return (
        <div
            className={`testimonials-avatar ${sizeClass}`}
            style={{ borderColor: avatarColor, color: avatarColor }}
            aria-hidden="true"
        >
            {getInitials(item.name)}
        </div>
    );
}

const Testimonials = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const { store, ready } = useStorefront();
    const languageKey = i18n.language.split('-')[0];

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [pauseAutoplay, setPauseAutoplay] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);
    const sectionRef = useRef(null);
    const gridRef = useRef(null);
    const miniCardRefs = useRef({});
    const guestListHeadingId = useId();

    const title = t('testimonials.title', { defaultValue: 'What Our Guests Say' });
    const description = t('testimonials.description', {
        defaultValue: 'Real words from people who dined with us.',
    });
    const eyebrow = t('testimonials.eyebrow', { defaultValue: 'Testimonials' });
    const guestListLabel = t('testimonials.guestListLabel', { defaultValue: 'All guest stories' });

    useEffect(() => {
        if (!ready || !store?.id) return undefined;

        let cancelled = false;

        (async () => {
            try {
                setLoading(true);
                const data = await fetchTestimonials(store.id);
                if (cancelled) return;

                const mapped = sortTestimonials(
                    data
                        .map((item) => mapTestimonialRecord(item, languageKey))
                        .filter((item) => item?.name && item?.message),
                );

                setItems(mapped.length > 0 ? mapped : getTestimonialsPlaceholder(t));
                setActiveIndex(0);
            } catch {
                if (!cancelled) setItems(getTestimonialsPlaceholder(t));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [ready, store?.id, languageKey, t]);

    const goPrev = useCallback(() => {
        setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
    }, [items.length]);

    const goNext = useCallback(() => {
        setActiveIndex((prev) => (prev + 1) % items.length);
    }, [items.length]);

    useEffect(() => {
        if (loading || items.length === 0) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) setIsVisible(true);
            },
            { threshold: 0.15 },
        );

        const section = sectionRef.current;
        if (section) {
            observer.observe(section);
            // If the section is already on screen when data arrives, reveal it immediately.
            const rect = section.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
                setIsVisible(true);
            }
        }

        return () => observer.disconnect();
    }, [loading, items.length]);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => setReducedMotion(mq.matches);
        sync();
        mq.addEventListener('change', sync);
        return () => mq.removeEventListener('change', sync);
    }, []);

    useEffect(() => {
        if (reducedMotion || pauseAutoplay || items.length <= 1) return undefined;
        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % items.length);
        }, 5200);
        return () => clearInterval(interval);
    }, [items.length, reducedMotion, pauseAutoplay]);

    useEffect(() => {
        const grid = gridRef.current;
        const el = miniCardRefs.current[activeIndex];
        if (!grid || !el) return;

        const pad = 8;
        const gr = grid.getBoundingClientRect();
        const er = el.getBoundingClientRect();
        let delta = 0;
        if (er.top < gr.top + pad) {
            delta = er.top - gr.top - pad;
        } else if (er.bottom > gr.bottom - pad) {
            delta = er.bottom - gr.bottom + pad;
        }
        if (delta !== 0) {
            grid.scrollBy({
                top: delta,
                behavior: reducedMotion ? 'auto' : 'smooth',
            });
        }
    }, [activeIndex, reducedMotion]);

    if (loading || !title || items.length === 0) return null;

    const isRtl = dir() === 'rtl';
    const onSectionKeyDown = (e) => {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            isRtl ? goNext() : goPrev();
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            isRtl ? goPrev() : goNext();
        }
    };

    const featuredItem = items[activeIndex];
    const visibleDotIndices = getVisibleDotIndices(activeIndex, items.length);

    return (
        <section
            ref={sectionRef}
            className={`section section--surface-2 testimonials-section${isVisible ? ' testimonials-section--visible' : ''}`}
            id="testimonials"
            dir={dir()}
            tabIndex={0}
            onKeyDown={onSectionKeyDown}
        >
            <div className="testimonials-bg-pattern" aria-hidden="true">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className={`testimonials-bg-circle testimonials-bg-circle--${i + 1}`} />
                ))}
            </div>

            <div className="testimonials-container">
                <div className="testimonials-header">
                    <span className="testimonials-eyebrow">{eyebrow}</span>
                    <h2 className="testimonials-title">{title}</h2>
                    <p className="testimonials-description">{description}</p>
                </div>

                <div
                    className="testimonials-layout"
                    onMouseEnter={() => setPauseAutoplay(true)}
                    onMouseLeave={() => setPauseAutoplay(false)}
                >
                    <div className="testimonials-featured">
                        <div
                            className="testimonials-featured-card"
                            key={featuredItem.id || activeIndex}
                            role="group"
                            aria-roledescription="carousel"
                            aria-label={eyebrow}
                        >
                            <QuoteIcon />
                            <p className="testimonials-featured-message">{featuredItem.message}</p>
                            <div className="testimonials-featured-author">
                                <TestimonialAvatar
                                    item={featuredItem}
                                    index={activeIndex}
                                    sizeClass="testimonials-avatar--lg"
                                />
                                <div className="testimonials-author-info">
                                    <strong className="testimonials-author-name">{featuredItem.name}</strong>
                                    <span className="testimonials-author-role">{featuredItem.role}</span>
                                    <StarRating rating={featuredItem.rating} />
                                </div>
                            </div>
                        </div>

                        <div className="testimonials-featured-toolbar">
                            <div className="testimonials-dots" aria-label={eyebrow}>
                                {visibleDotIndices.map((i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        className={`testimonials-dot${i === activeIndex ? ' testimonials-dot--active' : ''}`}
                                        onClick={() => setActiveIndex(i)}
                                        aria-label={t('testimonials.goTo', { n: i + 1 })}
                                        aria-current={i === activeIndex ? 'true' : undefined}
                                    />
                                ))}
                            </div>
                            <div className="testimonials-nav-arrows">
                                <button
                                    type="button"
                                    className="testimonials-nav-btn"
                                    onClick={goPrev}
                                    aria-label={t('testimonials.prev')}
                                >
                                    <ChevronIcon direction="prev" />
                                </button>
                                <button
                                    type="button"
                                    className="testimonials-nav-btn"
                                    onClick={goNext}
                                    aria-label={t('testimonials.next')}
                                >
                                    <ChevronIcon direction="next" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <aside className="testimonials-aside" aria-labelledby={guestListHeadingId}>
                        <h3 id={guestListHeadingId} className="testimonials-aside-title">
                            {guestListLabel}
                        </h3>
                        <div className="testimonials-grid" ref={gridRef}>
                            {items.map((item, index) => {
                                const isActive = index === activeIndex;
                                return (
                                    <button
                                        key={item.id || `${item.name}-${index}`}
                                        ref={(el) => {
                                            miniCardRefs.current[index] = el;
                                        }}
                                        type="button"
                                        className={`testimonials-mini-card${isActive ? ' testimonials-mini-card--active' : ''}${hoveredIndex === index ? ' testimonials-mini-card--hovered' : ''}`}
                                        onClick={() => setActiveIndex(index)}
                                        onMouseEnter={() => setHoveredIndex(index)}
                                        onMouseLeave={() => setHoveredIndex(null)}
                                        style={{ '--delay': `${Math.min(index, 12) * 0.05}s` }}
                                        aria-current={isActive ? 'true' : undefined}
                                    >
                                        <TestimonialAvatar
                                            item={item}
                                            index={index}
                                            sizeClass="testimonials-avatar--sm"
                                        />
                                        <div className="testimonials-mini-info">
                                            <strong className="testimonials-mini-name">{item.name}</strong>
                                            <span className="testimonials-mini-role">{item.role}</span>
                                        </div>
                                        {isActive && (
                                            <span className="testimonials-mini-indicator" aria-hidden="true" />
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </aside>
                </div>
            </div>
        </section>
    );
};

export default Testimonials;
